import { useEvent } from '@emerald-works/react-event-bus-client'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { BannerStatic } from '../../../components'
import { useExternalApp } from '../../../hooks'
import { UserListSlice } from '../../../reducers'
import useConsumerTenant from '../../consumer-list/useConsumerTenant'
import { CONSTANTS } from '../../../util/constants'

const getChangedFields = (dirty, data) => {
  const dirtyFields = Object.keys(dirty)
  return dirtyFields.reduce((acc, field) => {
    acc[field] = data[field]
    return acc
  }, {})
}

const useDetailForm = () => {
  const { detail } = useSelector(UserListSlice.selectors.selectUser)

  const form = useForm()
  const dirtyFields = form.formState.dirtyFields
  const userPoolId = useConsumerTenant()
  const dispatch = useDispatch()

  const { baseToolkitUrl } = useExternalApp({ excludeProtocol: true })

  const [isEdit, setIsEdit] = React.useState(false)

  const [updateRoleUser, updateUser] = useEvent([
    {
      eventName: 'adminUpdateRoleUser',
      onSuccess: payload => {
        dispatch(UserListSlice.actions.setUserRole(payload.role))
        BannerStatic.show({ label: 'User role changed successfully' })
        setIsEdit(false)
      },
      onError: e => {
        console.error('Error while attempting to change the user role', e)
        BannerStatic.show({
          label: 'Error while attempting to change the user role',
          isError: true
        })
      }
    },
    {
      eventName: 'adminUpdateUser',
      onSuccess: (payload) => {
        const { errors } = payload
        const label = errors.length > 0 ? `User updated with errors: ${errors.toString()}` : 'User updated successfully'
        const updatedFields = getChangedFields(
          form.formState.dirtyFields,
          form.getValues()
        )
        delete updatedFields.role
        dispatch(UserListSlice.actions.setUserDetail(updatedFields))
        BannerStatic.show({ label })
        setIsEdit(false)
      },
      onError: e => {
        console.error("Error while attempting to update the user's details", e)
        BannerStatic.show({
          label: "Error while attempting to update the user's details",
          isError: true
        })
      }
    }
  ])

  const resetForm = React.useCallback(() => {
    form.reset({
      role: detail.roles?.[0],
      givenName: detail.givenName,
      familyName: detail.familyName,
      email: detail.email
    })
  }, [detail, form])

  React.useEffect(() => {
    if (detail) {
      resetForm()
    }
  }, [detail, resetForm])

  const handleEdit = () => {
    setIsEdit(true)
  }
  const handleCancelEdit = () => {
    setIsEdit(false)
    resetForm()
  }

  const loading = updateRoleUser.isWorking || updateUser.isWorking

  const onSubmit = data => {
    const fieldsToUpdate = getChangedFields(dirtyFields, data)

    const { role, ...other } = fieldsToUpdate

    if (role) {
      updateRoleUser.trigger({
        userPoolId,
        username: detail.username,
        role: data.role,
        previousRole: detail.roles?.[0]
      })
    }

    if (Object.keys(other).length) {
      const { givenName, familyName, email } = data
      updateUser.trigger({
        username: detail.username,
        tenantId: CONSTANTS.DEFAULT_CLUB_PK,
        attributes: {
          given_name: givenName,
          family_name: familyName,
          email
        },
        verifyUrl: baseToolkitUrl
      })
    }
  }

  return { ...form, onSubmit, loading, isEdit, handleEdit, handleCancelEdit }
}

export default useDetailForm
